import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, Eye, FileText, Link2, Pencil, PenLine, Plus, ScanLine, Search, Trash2, Unlink } from 'lucide-react';
import type { Weighing } from '@container-system/shared';
import * as weighingsApi from '../../api/weighings';
import { useAuth } from '../../app/AuthContext';
import { useSocketEvent } from '../../app/socket';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/DataTable';
import { Input } from '../../components/ui/Input';
import { EmptyState } from '../../components/ui/EmptyState';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../components/ui/Toast';
import { formatDate, formatDateTime, formatTime, formatWeight } from '../../utils/format';
import { documentUrl } from '../../api/documents';

const PAGE_SIZE = 25;

/** Fasst aufeinanderfolgende Wiegungen desselben Kalendertags zusammen - die Liste kommt vom Backend bereits absteigend nach `dateTime` sortiert, gleiche Tage bleiben dadurch garantiert zusammenhängend. */
function groupByDay(items: Weighing[]): { dateLabel: string; items: Weighing[] }[] {
  const groups: { dateLabel: string; items: Weighing[] }[] = [];
  for (const item of items) {
    const label = formatDate(item.dateTime);
    const current = groups.at(-1);
    if (current && current.dateLabel === label) current.items.push(item);
    else groups.push({ dateLabel: label, items: [item] });
  }
  return groups;
}

interface TourBracket {
  tourId: string;
  top: number;
  bottom: number;
}

/**
 * Misst die tatsächliche vertikale Position von je zwei Wiegungen derselben Tour (können
 * durch andere Zeilen oder einen Tages-Gruppenbalken getrennt sein) relativ zum Tabellen-
 * Wrapper - daraus lässt sich die verbindende Klammer am linken Rand zeichnen. Nur für
 * Touren, bei denen BEIDE Wiegungen auf der aktuellen Seite sichtbar sind; liegt der Partner
 * auf einer anderen Seite, bleibt nur das grüne Verkettungs-Icon als Hinweis (wie bisher).
 */
function useTourBrackets(items: Weighing[], layoutVersion: unknown) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const [brackets, setBrackets] = useState<TourBracket[]>([]);

  useLayoutEffect(() => {
    function recompute() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const wrapTop = wrap.getBoundingClientRect().top;
      const byTour = new Map<string, number[]>();
      for (const w of items) {
        if (!w.tourId) continue;
        const el = rowRefs.current.get(w.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const centerY = rect.top - wrapTop + rect.height / 2;
        const list = byTour.get(w.tourId) ?? [];
        list.push(centerY);
        byTour.set(w.tourId, list);
      }
      const next: TourBracket[] = [];
      for (const [tourId, ys] of byTour) {
        if (ys.length !== 2) continue;
        next.push({ tourId, top: Math.min(ys[0]!, ys[1]!), bottom: Math.max(ys[0]!, ys[1]!) });
      }
      setBrackets(next);
    }
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
    // `layoutVersion` (z.B. `collapsedDays`) hat keinen Einfluss auf `items` selbst, verschiebt
    // aber die tatsächliche Zeilenposition (ein-/ausgeklappte Tage) - muss die Bögen deshalb
    // trotzdem neu berechnen lassen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, layoutVersion]);

  const setRowRef = (id: string) => (el: HTMLTableRowElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  };

  return { wrapRef, setRowRef, brackets };
}

const BRACKET_GUTTER_WIDTH = 40;
const BRACKET_EDGE_X = 34;
const BRACKET_MIN_BULGE = 8;
const BRACKET_MAX_BULGE = 30;
/** Abstand, ab dem der Bogen seine maximale Ausbuchtung erreicht - bewusst klein (~3 Zeilen)
 *  gewählt, damit der Unterschied schon bei alltäglichen Abständen deutlich sichtbar ist,
 *  statt erst bei sehr weit auseinanderliegenden Paaren aufzufallen. */
const BRACKET_REFERENCE_SPAN = 150;

/**
 * Bei mehreren Touren auf einer Seite - v.a. verschachtelten (z.B. Zeile 1+4 UND Zeile 2+3) -
 * sahen alle Bögen bei gleicher Ausbuchtungstiefe wie EIN verschwommener Strich aus, man
 * konnte die einzelnen Paare nicht mehr auseinanderhalten. Jetzt buchtet ein Bogen umso
 * weiter nach links aus, je weiter die beiden Zeilen auseinanderliegen (Wurzel-Skalierung,
 * damit auch sehr weit auseinanderliegende Paare noch in die schmale Randspalte passen) -
 * benachbarte Paare bleiben eng am Rand, weiter entfernte wölben sich sichtbar weiter raus.
 */
function bulgeForSpan(span: number): number {
  const t = Math.min(1, span / BRACKET_REFERENCE_SPAN);
  return BRACKET_MIN_BULGE + (BRACKET_MAX_BULGE - BRACKET_MIN_BULGE) * Math.sqrt(t);
}

/** Ein nach links ausbuchtender Bogen zwischen zwei Zeilen - visualisiert, welche zwei Wiegungen als Tour verbunden sind. Ausbuchtungstiefe wächst mit dem Zeilenabstand, damit verschachtelte Touren unterscheidbar bleiben. */
function TourBracketOverlay({ brackets }: { brackets: TourBracket[] }) {
  if (brackets.length === 0) return null;
  return (
    <svg className="pointer-events-none absolute left-0 top-0 h-full" width={BRACKET_GUTTER_WIDTH} aria-hidden="true">
      {brackets.map((b) => {
        const bulgeX = BRACKET_EDGE_X - bulgeForSpan(b.bottom - b.top);
        return (
          <path
            key={b.tourId}
            d={`M ${BRACKET_EDGE_X} ${b.top} C ${bulgeX} ${b.top}, ${bulgeX} ${b.bottom}, ${BRACKET_EDGE_X} ${b.bottom}`}
            stroke="#fb923c"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        );
      })}
      {brackets.map((b) => (
        <Fragment key={`${b.tourId}-dots`}>
          <circle cx={BRACKET_EDGE_X} cy={b.top} r={2.5} fill="#fb923c" />
          <circle cx={BRACKET_EDGE_X} cy={b.bottom} r={2.5} fill="#fb923c" />
        </Fragment>
      ))}
    </svg>
  );
}

export function WeighingsListPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('ADMIN', 'OPERATOR');
  const isAdmin = hasRole('ADMIN');
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Weighing | null>(null);
  const [detailTarget, setDetailTarget] = useState<Weighing | null>(null);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ['weighings', 'list', page, search],
    queryFn: () => weighingsApi.listWeighings({ page, pageSize: PAGE_SIZE, search: search || undefined }),
  });

  useSocketEvent('weighing:created', () => void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] }));
  useSocketEvent('weighing:updated', () => void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] }));
  useSocketEvent('weighing:deleted', () => void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] }));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => weighingsApi.deleteWeighing(id),
    onSuccess: () => {
      toast.show('Wiegung gelöscht.', 'success');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] });
    },
    onError: () => toast.show('Löschen fehlgeschlagen.', 'error'),
  });

  const pairMutation = useMutation({
    mutationFn: () => weighingsApi.pairTour(selected as [string, string]),
    onSuccess: () => {
      toast.show('Tour verbunden.', 'success');
      setSelected([]);
      void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] });
    },
    onError: () => toast.show('Verbinden fehlgeschlagen.', 'error'),
  });

  const unpairMutation = useMutation({
    mutationFn: (id: string) => weighingsApi.unpairTour(id),
    onSuccess: () => {
      toast.show('Tour getrennt.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['weighings', 'list'] });
    },
  });

  function toggleSelect(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 2 ? [...prev, id] : [prev[1]!, id]));
  }

  function toggleDay(dateLabel: string) {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateLabel)) next.delete(dateLabel);
      else next.add(dateLabel);
      return next;
    });
  }

  const groups = groupByDay(query.data?.items ?? []);
  const { wrapRef, setRowRef, brackets } = useTourBrackets(query.data?.items ?? [], collapsedDays);
  // Bogen-Rand, Details, Typ, Datum, TKS, Kennzeichen, Werk, Container, Material, Legierung, Gewicht, Aktionen (+ Checkbox nur mit Schreibrecht)
  const columnCount = 12 + (canWrite ? 1 : 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Wiegungen"
        description={`${query.data?.total ?? 0} Einträge gesamt`}
        actions={
          <>
            {selected.length === 2 && (
              <Button size="sm" variant="secondary" onClick={() => pairMutation.mutate()} loading={pairMutation.isPending}>
                <Link2 className="h-4 w-4" strokeWidth={1.75} /> Zu Tour verbinden
              </Button>
            )}
            {canWrite && (
              <Link to="/ocr">
                <Button size="sm" variant="violet">
                  <ScanLine className="h-4 w-4" strokeWidth={1.75} /> OCR-Wiegung
                </Button>
              </Link>
            )}
            {canWrite && (
              <Link to="/weighings/new">
                <Button size="sm">
                  <Plus className="h-4 w-4" strokeWidth={2} /> Neue Wiegung
                </Button>
              </Link>
            )}
          </>
        }
      />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <Input
            placeholder="Suche nach Kennzeichen, Container, Material, Bemerkung…"
            icon={Search}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          {query.isLoading ? (
            <FullPageSpinner />
          ) : groups.length === 0 ? (
            <EmptyState title="Keine Wiegungen gefunden." />
          ) : (
            <>
              <div ref={wrapRef} className="relative overflow-x-auto">
                {/* Feste 13.5px statt der sonst üblichen text-sm - erst dadurch passen alle
                    Spalten auf typische Desktop-Breiten, ohne dass horizontal gescrollt werden
                    muss (Details, Typ, Uhrzeit, TKS, Kennzeichen, Werk, Container, Material,
                    Legierung, Gewicht, Aktionen sind zusammen sonst zu breit). */}
                <table className="w-full text-left text-[13.5px]">
                  <thead>
                    <tr className="bg-accent-500/15 text-xs font-semibold uppercase text-accent-300 max-md:text-[11px]">
                      {/* Schmale Randspalte - hier zeichnet TourBracketOverlay den verbindenden Bogen zwischen zwei zur Tour gehörenden Zeilen. */}
                      <th className="w-10" />
                      <th className="w-8 px-2 py-2.5" />
                      {canWrite && <th className="w-8 px-2 py-2.5" />}
                      <th className="whitespace-nowrap px-2 py-2.5">Typ</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Uhrzeit</th>
                      <th className="whitespace-nowrap px-2 py-2.5">TKS</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Kennzeichen</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Werk</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Container</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Material</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Legierung</th>
                      <th className="whitespace-nowrap px-2 py-2.5">Gewicht (t)</th>
                      <th className="whitespace-nowrap px-2 py-2.5 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => {
                      const isCollapsed = collapsedDays.has(group.dateLabel);
                      return (
                      <Fragment key={group.dateLabel}>
                        <tr>
                          <td colSpan={columnCount} className="bg-accent-600 p-0 text-xs font-semibold uppercase tracking-wide text-white">
                            <button
                              type="button"
                              onClick={() => toggleDay(group.dateLabel)}
                              className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left hover:bg-white/10"
                            >
                              <ChevronDown className={clsx('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')} strokeWidth={2} />
                              {group.dateLabel} <span className="text-white/80">· {group.items.length} Einträge</span>
                            </button>
                          </td>
                        </tr>
                        {!isCollapsed && group.items.map((w) => (
                          <tr key={w.id} ref={setRowRef(w.id)} className="border-b border-surface-700 hover:bg-accent-500/5">
                            <td />
                            <td className="px-2 py-2.5">
                              <button
                                title="Details anzeigen"
                                aria-label="Details anzeigen"
                                className="rounded-md p-1 text-ivory-500 hover:bg-surface-700 hover:text-accent-300"
                                onClick={() => setDetailTarget(w)}
                              >
                                <Eye className="h-4 w-4" strokeWidth={1.75} />
                              </button>
                            </td>
                            {canWrite && (
                              <td className="px-2 py-2.5">
                                {!w.tourId && (
                                  <input type="checkbox" checked={selected.includes(w.id)} onChange={() => toggleSelect(w.id)} />
                                )}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-2 py-2.5">
                              <span
                                title={w.source === 'OCR' ? 'OCR-Erfassung' : 'Manuelle Erfassung'}
                                className={clsx(
                                  'inline-flex h-7 w-7 items-center justify-center gap-1.5 rounded-full',
                                  w.source === 'OCR' ? 'bg-violet-500/15 text-violet-300' : 'bg-surface-700 text-ivory-400',
                                )}
                              >
                                {w.source === 'OCR' ? <ScanLine className="h-4 w-4 shrink-0" strokeWidth={1.75} /> : <PenLine className="h-4 w-4 shrink-0" strokeWidth={1.75} />}
                              </span>
                              {w.tourId && <Link2 className="ml-1 inline h-3.5 w-3.5 shrink-0 text-success-400" strokeWidth={1.75} />}
                            </td>
                            {/* Nur die Uhrzeit - das volle Datum steht bereits einmal im Tages-Gruppenbalken darüber, eine Wiederholung pro Zeile kostet nur Spaltenbreite. */}
                            <td className="whitespace-nowrap px-2 py-2.5 font-mono text-ivory-300">{formatTime(w.dateTime)}</td>
                            <td className="whitespace-nowrap px-2 py-2.5 font-mono text-ivory-300">{w.gateNumber ?? '—'}</td>
                            <td className="whitespace-nowrap px-2 py-2.5 font-mono text-ivory-300">{w.licensePlate ?? '—'}</td>
                            <td className="whitespace-nowrap px-2 py-2.5">{w.plant.name}</td>
                            <td className="whitespace-nowrap px-2 py-2.5 font-mono">{w.container.name}</td>
                            <td className="whitespace-nowrap px-2 py-2.5">{w.material.name}</td>
                            <td className="whitespace-nowrap px-2 py-2.5 text-ivory-300">{w.alloy?.name ?? '—'}</td>
                            <td className="whitespace-nowrap px-2 py-2.5 font-mono font-semibold text-ivory-100">{formatWeight(w.netWeightKg)}</td>
                            <td className="whitespace-nowrap px-2 py-2.5">
                              <div className="flex items-center justify-end gap-0.5">
                                {w.document && (
                                  <a
                                    href={documentUrl(w.document.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Dokument ansehen"
                                    className="rounded-md p-1.5 md:p-0.5 text-accent-400 hover:bg-surface-700 hover:text-accent-300"
                                  >
                                    <FileText className="h-4 w-4" strokeWidth={1.75} />
                                  </a>
                                )}
                                {canWrite && w.tourId && (
                                  <button
                                    title="Von Tour trennen"
                                    className="rounded-md p-1.5 md:p-0.5 text-ivory-500 hover:bg-surface-700 hover:text-ivory-100"
                                    onClick={() => unpairMutation.mutate(w.id)}
                                  >
                                    <Unlink className="h-4 w-4" strokeWidth={1.75} />
                                  </button>
                                )}
                                {canWrite && (
                                  <button
                                    title="Bearbeiten"
                                    aria-label="Bearbeiten"
                                    className="rounded-md p-1.5 md:p-0.5 text-accent-400 hover:bg-surface-700 hover:text-accent-300"
                                    onClick={() => navigate(`/weighings/${w.id}/edit`)}
                                  >
                                    <Pencil className="h-4 w-4" strokeWidth={1.75} />
                                  </button>
                                )}
                                {isAdmin && (
                                  <button
                                    title="Löschen"
                                    aria-label="Löschen"
                                    className="rounded-md p-1.5 md:p-0.5 text-danger-400 hover:bg-surface-700 hover:text-danger-300"
                                    onClick={() => setDeleteTarget(w)}
                                  >
                                    <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <TourBracketOverlay brackets={brackets} />
              </div>
              <Pagination page={page} totalPages={query.data?.totalPages ?? 1} onChange={setPage} />
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Wiegung löschen"
        message={`Möchtest du die Wiegung von "${deleteTarget?.container.name}" (${deleteTarget ? formatWeight(deleteTarget.netWeightKg) : ''}) unwiderruflich löschen?`}
        confirmLabel="Löschen"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {detailTarget && (
        <WeighingDetailModal
          weighing={detailTarget}
          canWrite={canWrite}
          onClose={() => setDetailTarget(null)}
          onEdit={() => {
            const id = detailTarget.id;
            setDetailTarget(null);
            navigate(`/weighings/${id}/edit`);
          }}
        />
      )}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ivory-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-ivory-100">{children}</dd>
    </div>
  );
}

function WeighingDetailModal({
  weighing: w,
  canWrite,
  onClose,
  onEdit,
}: {
  weighing: Weighing;
  canWrite: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Details zur Wiegung"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          {canWrite && (
            <Button onClick={onEdit}>
              <Pencil className="h-4 w-4" strokeWidth={1.75} /> Bearbeiten
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
              w.source === 'OCR' ? 'bg-violet-500/15 text-violet-300' : 'bg-surface-700 text-ivory-300',
            )}
          >
            {w.source === 'OCR' ? <ScanLine className="h-3.5 w-3.5" strokeWidth={1.75} /> : <PenLine className="h-3.5 w-3.5" strokeWidth={1.75} />}
            {w.source === 'OCR' ? 'OCR-Erfassung' : 'Manuelle Erfassung'}
          </span>
          {w.tourId && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success-500/15 px-2.5 py-1 text-xs font-medium text-success-400">
              <Link2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Teil einer Tour
            </span>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <DetailRow label="Datum & Uhrzeit">{formatDateTime(w.dateTime)}</DetailRow>
          <DetailRow label="TKS">{w.gateNumber ?? '—'}</DetailRow>
          <DetailRow label="Kennzeichen">{w.licensePlate ?? '—'}</DetailRow>
          <DetailRow label="Werk">{w.plant.name}</DetailRow>
          <DetailRow label="Container">{w.container.name}</DetailRow>
          <DetailRow label="Material">{w.material.name}</DetailRow>
          <DetailRow label="Legierung">{w.alloy?.name ?? '—'}</DetailRow>
          <DetailRow label="Gewicht">{formatWeight(w.netWeightKg)}</DetailRow>
          <DetailRow label="LKW-Fahrer">{w.driver?.name ?? '—'}</DetailRow>
          <DetailRow label="Erfasst am">{formatDateTime(w.createdAt)}</DetailRow>
        </dl>

        <div>
          <p className="text-xs text-ivory-500">Bemerkung</p>
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-ivory-100">{w.remarks ?? '—'}</p>
        </div>

        {w.document && (
          <a
            href={documentUrl(w.document.id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 self-start rounded-md border border-surface-600 px-3 py-1.5 text-sm font-medium text-accent-400 hover:bg-surface-700"
          >
            <FileText className="h-4 w-4" strokeWidth={1.75} /> Dokument ansehen
          </a>
        )}
      </div>
    </Modal>
  );
}
